import { useState, useEffect, useCallback } from 'react';

// =====================================================================
// useCalendarioMes — as camadas INTERNAS do calendário, por mês
// =====================================================================
// Busca as três coisas que o portal sabe sobre um mês:
//   - aniversariantes (/api/home/aniversariantes?mes=N) — recorrente (só dia/mês)
//   - feriados        (/api/calendario/feriados?ano&mes) — rede toda
//   - eventos         (/api/calendario/eventos?ano&mes)  — com recorte perfil/loja
//
// Extraído da tela /aniversariantes quando o modal do ícone de calendário passou
// a mostrar as MESMAS camadas (mais os compromissos do Teams, que vêm do
// useAgendaMs — outro hook, outro backend). Sem isto, as três buscas e o
// agrupamento por dia nasceriam duplicados nas duas telas.
//
// Falha silenciosa por camada: se uma cair, as outras seguem — o calendário é
// leitura, não pode quebrar inteiro porque um endpoint tossiu.
//
// Devolve as listas cruas, os mapas já agrupados POR DIA do mês (que é como o
// grid consome), as `opcoes` de compartilhamento e os recarregadores (as duas
// telas cadastram feriado/evento e precisam refazer a busca).

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

export default function useCalendarioMes(ano, mes) {
    const [aniversariantes, setAniversariantes] = useState([]);
    const [feriados, setFeriados] = useState([]);
    const [eventos, setEventos] = useState([]);
    // O que o formulário de evento precisa saber: os usuários (com quem dá para
    // compartilhar; o `perfil`/`loja` de cada um alimenta os filtros da tela) +
    // duas respostas que só o backend dá — `podeCriar` (regra escritório/gerente)
    // e `msConectado` (tem conta Microsoft vinculada?).
    // Nada disso depende do mês — carga única, fora do efeito que reage a ano/mes.
    // O default é o estado MAIS RESTRITO: enquanto a busca não volta, não se
    // oferece um botão de criar que pode não existir para esta pessoa.
    const [opcoes, setOpcoes] = useState({ usuarios: [], podeCriar: false, msConectado: false });
    const [carregando, setCarregando] = useState(true);

    const carregarAniversariantes = useCallback(async () => {
        try {
            const r = await fetch(`/api/home/aniversariantes?mes=${mes}`, { headers: authHeaders() });
            setAniversariantes(r.ok ? await r.json() : []);
        } catch (e) {
            console.error('[Calendário] aniversariantes:', e);
            setAniversariantes([]);
        }
    }, [mes]);

    const carregarFeriados = useCallback(async () => {
        try {
            const r = await fetch(`/api/calendario/feriados?ano=${ano}&mes=${mes}`, { headers: authHeaders() });
            setFeriados(r.ok ? await r.json() : []);
        } catch (e) {
            console.error('[Calendário] feriados:', e);
            setFeriados([]);
        }
    }, [ano, mes]);

    const carregarEventos = useCallback(async () => {
        try {
            const r = await fetch(`/api/calendario/eventos?ano=${ano}&mes=${mes}`, { headers: authHeaders() });
            setEventos(r.ok ? await r.json() : []);
        } catch (e) {
            console.error('[Calendário] eventos:', e);
            setEventos([]);
        }
    }, [ano, mes]);

    useEffect(() => {
        let vivo = true;
        setCarregando(true);
        Promise.all([carregarAniversariantes(), carregarFeriados(), carregarEventos()])
            .finally(() => { if (vivo) setCarregando(false); });
        return () => { vivo = false; };
    }, [carregarAniversariantes, carregarFeriados, carregarEventos]);

    // Opções de compartilhamento — carga única (não variam com o mês).
    useEffect(() => {
        (async () => {
            try {
                const r = await fetch('/api/calendario/opcoes', { headers: authHeaders() });
                if (r.ok) setOpcoes(await r.json());
            } catch (e) { console.error('[Calendário] opcoes:', e); }
        })();
    }, []);

    // ---- Agrupamento por dia do mês (formato que o grid consome) ----------
    const anivPorDia = {};
    aniversariantes.forEach((a) => { (anivPorDia[a.dia] = anivPorDia[a.dia] || []).push(a); });

    const feriadosPorDia = {};
    feriados.forEach((f) => { (feriadosPorDia[f.dia] = feriadosPorDia[f.dia] || []).push(f); });

    // Evento pode ser PERÍODO: aparece em CADA dia do intervalo que cai neste mês
    // (um evento que começa mês passado e termina neste é pintado do dia 1 em diante).
    const diasNoMes = new Date(ano, mes, 0).getDate();
    const mesStr = `${ano}-${String(mes).padStart(2, '0')}`;
    const eventosPorDia = {};
    eventos.forEach((ev) => {
        const ini = ev.dataInicio;
        const fim = ev.dataFim || ev.dataInicio;
        const dIni = ini.slice(0, 7) === mesStr ? parseInt(ini.slice(8, 10), 10) : 1;
        const dFim = fim.slice(0, 7) === mesStr ? parseInt(fim.slice(8, 10), 10) : diasNoMes;
        for (let d = Math.max(1, dIni); d <= Math.min(diasNoMes, dFim); d++) {
            (eventosPorDia[d] = eventosPorDia[d] || []).push(ev);
        }
    });

    return {
        aniversariantes, feriados, eventos, opcoes, carregando,
        anivPorDia, feriadosPorDia, eventosPorDia,
        recarregarFeriados: carregarFeriados,
        recarregarEventos: carregarEventos,
    };
}
